import React, { useRef, useState, useEffect } from "react";
import { Pencil, Upload, Eraser, RotateCcw } from "lucide-react";

/**
 * SignatureCapture — Composant pour capturer une signature avec 2 modes :
 *   • Dessiner (canvas)
 *   • Importer une image (PNG/JPG transparent recommandé)
 *
 * Émet automatiquement le data URL PNG via `onChange(dataUrl | null)`.
 * Utilisé dans Paramétrages → Signature (loueur).
 *
 * Props :
 *   - value : data URL initial (ou null)
 *   - onChange : (dataUrl | null) => void  (appelé quand la valeur change)
 *   - label : titre affiché au-dessus (optionnel)
 *   - height : hauteur du canvas en px (défaut 160)
 *   - testid : préfixe data-testid
 */
export default function SignatureCapture({ value, onChange, label, height = 160, testid = "sig-capture" }) {
  const [mode, setMode] = useState(value ? "preview" : "draw"); // draw | upload | preview
  const [preview, setPreview] = useState(value || null);
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasStroke, setHasStroke] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setPreview(value || null);
    setMode(value ? "preview" : "draw");
  }, [value]);

  // Init canvas quand on entre en mode draw
  useEffect(() => {
    if (mode !== "draw") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0f172a";
    setHasStroke(false);
  }, [mode]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const t = e.touches?.[0];
    return {
      x: (t ? t.clientX : e.clientX) - rect.left,
      y: (t ? t.clientY : e.clientY) - rect.top,
    };
  };
  const startDraw = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setDrawing(true);
  };
  const draw = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasStroke(true);
  };
  const endDraw = () => {
    if (drawing) {
      setDrawing(false);
      if (hasStroke) {
        const data = canvasRef.current.toDataURL("image/png");
        setPreview(data);
        onChange?.(data);
      }
    }
  };
  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    setHasStroke(false);
    setPreview(null);
    onChange?.(null);
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      alert("Fichier trop volumineux (max 500 Ko)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      setPreview(dataUrl);
      onChange?.(dataUrl);
      setMode("preview");
    };
    reader.readAsDataURL(file);
  };

  const remove = () => {
    setPreview(null);
    onChange?.(null);
    setMode("draw");
  };

  return (
    <div data-testid={testid}>
      {label && <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 mb-2">{label}</div>}

      {mode === "preview" && preview ? (
        <div>
          <div className="rounded-xl border border-slate-200 bg-white p-4 flex items-center justify-center" style={{ minHeight: height }}>
            <img src={preview} alt={label || "Signature"} className="max-h-[140px] max-w-full object-contain" data-testid={`${testid}-preview`} />
          </div>
          <div className="mt-2 flex gap-2">
            <button type="button" onClick={remove} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-700 hover:bg-slate-50" data-testid={`${testid}-remove`}>
              <RotateCcw className="h-3 w-3" /> Redéfinir
            </button>
          </div>
        </div>
      ) : (
        <div>
          {/* Tabs Draw / Upload */}
          <div className="inline-flex items-center p-1 rounded-full bg-slate-100 mb-3">
            <button type="button" onClick={() => setMode("draw")} className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors ${mode === "draw" ? "bg-white shadow-sm text-slate-900" : "text-slate-600 hover:text-slate-900"}`} data-testid={`${testid}-tab-draw`}>
              <Pencil className="h-3 w-3" /> Dessiner
            </button>
            <button type="button" onClick={() => setMode("upload")} className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors ${mode === "upload" ? "bg-white shadow-sm text-slate-900" : "text-slate-600 hover:text-slate-900"}`} data-testid={`${testid}-tab-upload`}>
              <Upload className="h-3 w-3" /> Importer
            </button>
          </div>

          {mode === "draw" && (
            <div>
              <div className="rounded-xl border-2 border-dashed border-slate-300 bg-white overflow-hidden touch-none">
                <canvas
                  ref={canvasRef}
                  className="w-full block"
                  style={{ height }}
                  onMouseDown={startDraw}
                  onMouseMove={draw}
                  onMouseUp={endDraw}
                  onMouseLeave={endDraw}
                  onTouchStart={startDraw}
                  onTouchMove={draw}
                  onTouchEnd={endDraw}
                  data-testid={`${testid}-canvas`}
                />
              </div>
              <div className="mt-2 flex gap-2">
                <button type="button" onClick={clearCanvas} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-700 hover:bg-slate-50" data-testid={`${testid}-clear`}>
                  <Eraser className="h-3 w-3" /> Effacer
                </button>
                <span className="text-xs text-slate-400 self-center">Le tracé est enregistré automatiquement quand vous relâchez.</span>
              </div>
            </div>
          )}

          {mode === "upload" && (
            <div>
              <div
                className="rounded-xl border-2 border-dashed border-slate-300 bg-white flex flex-col items-center justify-center cursor-pointer hover:border-violet-400 transition-colors"
                style={{ minHeight: height }}
                onClick={() => fileInputRef.current?.click()}
                data-testid={`${testid}-upload-zone`}
              >
                <Upload className="h-6 w-6 text-slate-400" />
                <div className="text-xs text-slate-600 mt-2">Cliquez pour importer</div>
                <div className="text-[10px] text-slate-400 mt-0.5">PNG ou JPG · Fond transparent recommandé · Max 500 Ko</div>
              </div>
              <input ref={fileInputRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={onFile} data-testid={`${testid}-file-input`} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
